import {
  paletteExtended
} from "./chunk-GP7ZEBHS.js";
import {
  color,
  color2,
  darkCategoricalColor,
  lightCategoricalColor
} from "./chunk-KBRIPOOD.js";
import {
  font
} from "./chunk-WJHMXJ4P.js";
import {
  getThemeObjectTokens,
  getTypographyStyle
} from "./chunk-BPL7EIXS.js";
import {
  deepmerge
} from "./chunk-5OZHSW5X.js";

// src/theme/theme.ts
import { createTheme } from "@mui/material";
import varnishTokens from "@allenai/varnish-theme";
var getMuiCssBaselineStyleOverrides = (theme) => {
  return `
    a {
        color: ${theme.paletteExtended.link.default};
        text-decoration: none;
    }
    a:hover {
        text-decoration: underline;
    }
    .linkContrast {
        color: ${theme.paletteExtended.link.contrast};
    }
    ${getTypographyStyle(theme, "h1")}
    ${getTypographyStyle(theme, "h2")}
    ${getTypographyStyle(theme, "h3")}
    ${getTypographyStyle(theme, "h4")}
    ${getTypographyStyle(theme, "h5")}
    ${getTypographyStyle(theme, "h6")}
    p {
        ${getTypographyStyle(theme, "body1", false)}
        margin: 16px 0;
    }
    `;
};
var getVarnishDesignTokens = () => {
  return {
    components: {
      MuiTypography: {
        variants: [
          {
            props: { variant: "monospace" },
            style: {
              fontFamily: "monospace"
            }
          }
        ]
      },
      MuiContainer: {
        defaultProps: {
          disableGutters: true
        }
      },
      MuiCssBaseline: {
        styleOverrides: (theme) => getMuiCssBaselineStyleOverrides(theme)
      },
      MuiLink: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            textDecoration: "none",
            ...ownerState.variant === "dark" ? {
              color: theme.paletteExtended.link.contrast
            } : {
              color: theme.paletteExtended.link.default
            }
          })
        }
      },
      MuiRating: {
        styleOverrides: {
          root: {
            color: varnishTokens.palette.primary.main.value
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          sizeSmall: () => ({
            fontSize: varnishTokens.font.size.sm.value
          }),
          sizeLarge: ({ theme }) => ({
            fontSize: theme.typography.button.fontSize
          })
        }
      },
      MuiAppBar: {
        defaultProps: {
          position: "sticky"
        }
      }
    },
    typography: {
      fontFamily: varnishTokens.font.family.default.value,
      fontWeightLight: varnishTokens.font.weight.light.value,
      fontWeightRegular: varnishTokens.font.weight.regular.value,
      fontWeightMedium: varnishTokens.font.weight.regular.value,
      fontWeightBold: varnishTokens.font.weight.bold.value,
      h1: getThemeObjectTokens(varnishTokens.typography.h1),
      h2: getThemeObjectTokens(varnishTokens.typography.h2),
      h3: getThemeObjectTokens(varnishTokens.typography.h3),
      h4: getThemeObjectTokens(varnishTokens.typography.h4),
      h5: getThemeObjectTokens(varnishTokens.typography.h5),
      h6: getThemeObjectTokens(varnishTokens.typography.h6),
      subtitle1: getThemeObjectTokens(varnishTokens.typography.subtitle1),
      subtitle2: getThemeObjectTokens(varnishTokens.typography.subtitle2),
      body1: getThemeObjectTokens(varnishTokens.typography.body),
      body2: getThemeObjectTokens(varnishTokens.typography.bodySmall),
      button: {
        ...getThemeObjectTokens(varnishTokens.typography.button, [
          "fontFamily",
          "fontWeight",
          "fontSize",
          "lineHeight"
        ]),
        // fixes a mui issue requiring textTransform to not be a string
        textTransform: varnishTokens.typography.button.textTransform.value
      },
      caption: getThemeObjectTokens(varnishTokens.typography.caption),
      overline: {
        ...getThemeObjectTokens(varnishTokens.typography.overline, [
          "fontFamily",
          "fontWeight",
          "fontSize",
          "lineHeight"
        ]),
        // // fixes a mui issue requiring textTransform to not be a string
        textTransform: varnishTokens.typography.overline.textTransform.value
      }
    },
    palette: {
      mode: "light",
      primary: getThemeObjectTokens(varnishTokens.palette.primary),
      secondary: getThemeObjectTokens(varnishTokens.palette.secondary),
      error: getThemeObjectTokens(varnishTokens.palette.error),
      warning: getThemeObjectTokens(varnishTokens.palette.warning),
      info: getThemeObjectTokens(varnishTokens.palette.info),
      success: getThemeObjectTokens(varnishTokens.palette.success),
      grey: {
        50: color.N2.hex,
        100: color.N2.hex,
        200: color.N4.hex,
        300: color.N4.hex,
        400: color.N6.hex,
        500: color.N6.hex,
        600: color.N8.hex,
        700: color.N8.hex,
        800: color.N9.hex,
        900: color.N9.hex,
        A100: color.N2.hex,
        A200: color.N4.hex,
        A400: color.N6.hex,
        A700: color.N8.hex
      },
      text: getThemeObjectTokens(varnishTokens.palette.text),
      common: getThemeObjectTokens(varnishTokens.palette.common),
      divider: varnishTokens.palette.divider.value,
      background: getThemeObjectTokens(varnishTokens.palette.background)
    },
    spacing: varnishTokens.spacing.baseSpacingValue.value,
    paletteExtended,
    color,
    color2,
    darkCategoricalColor,
    lightCategoricalColor,
    font
  };
};
var getTheme = (overrides = {}) => {
  const mergedTheme = deepmerge(getVarnishDesignTokens(), overrides);
  return createTheme(mergedTheme);
};
var theme_default = { getVarnishDesignTokens, getTheme };

export {
  getVarnishDesignTokens,
  getTheme,
  theme_default
};
//# sourceMappingURL=chunk-B25DIOVL.js.map